import { create } from "zustand";
import apiManager from "utils/api/apiManager";
import {
  authLocalStorage,
  removeAuthFromLocalStorage,
} from "utils/helpers/auth";

type LoginResponse = {
  accessToken: string;
  refreshToken: string;
};

type User = {
  withContentCreatorRole: boolean;
  email: string;
  withCodeExporterRole: boolean;
  accessKeys?: string[];
};

type InitialState = {
  user: User | null;
  loading: boolean;
  getUserLoading: boolean;
  stopUserLoading: () => void;
  reset: () => void;
  getUser: () => Promise<void>;
  login: (
    emial: string,
    password: string,
    keepMe?: boolean
  ) => Promise<boolean>;
  forgotPassword: (email: string) => Promise<boolean>;
  resetPassword: (
    email: string,
    newPassword: string,
    resetToken: string
  ) => Promise<boolean>;
  changePassword: (
    oldPassword: string,
    newPassword: string
  ) => Promise<boolean>;

  logout: () => void;
};

const initialValues = {
  loading: false,
  getUserLoading: true,
  user: null,
};

export const useAuth = create<InitialState>((set, get) => ({
  ...initialValues,
  stopUserLoading: () => set({ getUserLoading: false }),
  reset: () => {
    set({ ...initialValues, getUserLoading: false });
  },
  getUser: async () => {
    set({ getUserLoading: true });
    try {
      const res = await apiManager.getData<User>("/api/identity/getUser");

      set({ user: res.response });
    } catch (err) {
      console.error("Error in data fetch:", err);
    } finally {
      set({ getUserLoading: false });
    }
  },
  login: async (email: string, password: string, keepMe: boolean = false) => {
    set({ loading: true });
    try {
      const res = await apiManager.postData<LoginResponse>(
        "/api/identity/login",
        {
          email,
          password,
          keepMe,
        }
      );

      if (res.isError) return res.isError;

      authLocalStorage({
        token: res.response?.accessToken ?? "",
        refreshToken: res.response?.refreshToken ?? "",
        keepMe: keepMe,
      });

      await get().getUser();

      return !res.isError;
    } catch (err) {
      console.error("Error in data fetch:", err);
      return false;
    } finally {
      set({ loading: false });
    }
  },
  forgotPassword: async (email: string) => {
    set({ loading: true });
    try {
      const res = await apiManager.postData("/api/identity/ForgotPassword", {
        email,
      });

      return !res.isError;
    } catch (err) {
      console.error("Error in data fetch:", err);
      return false;
    } finally {
      set({ loading: false });
    }
  },
  resetPassword: async (
    email: string,
    password: string,
    confirmationToken: string
  ) => {
    set({ loading: true });
    try {
      const res = await apiManager.postData("/api/identity/setPassword", {
        email,
        password,
        confirmationToken,
      });

      return !res.isError;
    } catch (err) {
      console.error("Error in data fetch:", err);
      return false;
    } finally {
      set({ loading: false });
    }
  },
  changePassword: async (oldPassword: string, newPassword: string) => {
    set({ loading: true });
    try {
      const res = await apiManager.postData("/api/identity/changePassword", {
        oldPassword,
        newPassword,
      });

      return !res.isError;
    } catch (err) {
      console.error("Error in data fetch:", err);
      return false;
    } finally {
      set({ loading: false });
    }
  },
  // non api call methods
  logout: () => {
    removeAuthFromLocalStorage();
    get().reset();
    window.location.reload();
  },
}));
